import { Draggable } from "@hello-pangea/dnd";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Button, ButtonBase, Checkbox, Collapse, IconButton, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, sortBy } from "lodash";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Doc } from "../../../common/js/icons";
import { noPhotoUrl } from "../utils/constants";
import { getFromAttributeValues } from "../utils/operations";
import schema from "./AuditSearchTables";

const StyledListingIdLink = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecorationColor: "#2C99D2",
    },

    color: "#2C99D2",
    fontWeight: 600,
    textDecoration: "underline",
    textDecorationColor: "blue",
});

const StyledDocumentLinkButton = styled(ButtonBase)({
    color: "#2C99D2",
    cursor: "pointer",
    fontWeight: 600,
    textDecoration: "none",
});

const StyledDocumentLinkSpan = styled("span")(({ document }) => {
    const mediaAssetKey = get(document, "mediaAssetKey");

    return {
        cursor: mediaAssetKey ? "pointer" : "default",
        color: mediaAssetKey ? "#2C99D2" : "grey",
        fontWeight: 600,
        textDecoration: "none",
    };
});

const StyledAuditSearchRowContainer = styled("div")({
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: "1vw",
    fontSize: "13px",
});

const StyledLb = styled(Link)({
    textDecoration: "none",
});

const StyledBrokerColumn = styled("div")({
    fontSize: "13px",
    marginLeft: "2px",
    "&>a:hover": {
        cursor: "default",
    },
});

const StyledTooltipSpan = styled("span")({
    "&>a:hover": {
        cursor: "default",
    },
});

const StyledLightButton = styled(Button)({
    fontWeight: "400",
    minWidth: "0px",
    margin: "0px",
    marginRight: "5px",
    padding: "5px 10px",
});

const AuditSearchRow = (props) => {
    const { viewDocument, comp, i, activeTab, toggleCompAuditFlag } = props;

    const [expanded, setExpanded] = useState(false);
    const [sortedDocuments, setSortedDocuments] = useState([]);
    const [isChecked, setIsChecked] = useState(comp.audit_flg);

    useEffect(() => {
        loadDocuments(get(comp, "attachedRequiredDocuments"), get(comp, "missingRequiredDocuments"));
    }, []);

    useEffect(() => {
        setIsChecked(comp.audit_flg);
    }, [comp.audit_flg]);

    const loadDocuments = (attached, missing) => {
        let aggregatedDocs = undefined;

        if (attached && missing) {
            aggregatedDocs = [...attached, ...missing];
        } else if (attached) {
            aggregatedDocs = attached;
        } else if (missing) {
            aggregatedDocs = missing;
        }
        setSortedDocuments(sortBy(aggregatedDocs, ["documentTypeName", "documentTypeKey"]));
    };

    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: "none",
        // change background colour if dragging
        background: isDragging ? "#C5C5C5" : "",

        // styles we need to apply on draggables
        ...draggableStyle,
    });

    const getTableCellFlex = (header) => {
        if (header === "Address") {
            return "2";
        } else if (header === "Documents") {
            return "5";
        } else {
            return "1";
        }
    };

    const getTableFixedWidth = (header) => {
        if (header === "Address") {
            return "200px";
        } else {
            return "100px";
        }
    };

    return (
        <Draggable draggableId={`AuditSearch Draggable - ${String(i)}`} index={i}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                >
                    <StyledAuditSearchRowContainer container key={`tr-${i}`} selectable="false">
                        {schema.viewHeaders(activeTab)["all_comps"].map((y, k) => {
                            return y.name === "ToggleToList" ? (
                                <div
                                    style={{
                                        paddingLeft: "25px",
                                        flex: getTableCellFlex(y.name),
                                        minWidth: getTableFixedWidth(y.name),
                                        maxWidth: getTableFixedWidth(y.name),
                                    }}
                                >
                                    <Checkbox
                                        data-testid="auditorSearchCheckbox"
                                        checked={isChecked}
                                        color="primary"
                                        onChange={() => {
                                            toggleCompAuditFlag(comp.dealkey);
                                        }}
                                    />
                                    <IconButton
                                        aria-label="expand row"
                                        size="small"
                                        onClick={() => setExpanded(!expanded)}
                                    >
                                        {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    </IconButton>
                                </div>
                            ) : y.name === "Property" ? (
                                <div
                                    key={`trc-${k}`}
                                    style={{
                                        flex: getTableCellFlex(y.name),
                                        marginLeft: "-50px",
                                        minWidth: getTableFixedWidth(y.name),
                                        maxWidth: getTableFixedWidth(y.name),
                                    }}
                                >
                                    <div
                                        className="propertySmallImageContainer"
                                        style={{
                                            width: "85px",
                                            maxWidth: "85px",
                                            height: "80px",
                                            margin: "10px 23px 10px 27px",
                                            backgroundImage: `url(${
                                                get(comp, "listing_photo", null) !== null
                                                    ? get(comp, "listing_photo", null)
                                                    : get(comp, "property_photo", null) !== null
                                                      ? get(comp, "property_photo", null)
                                                      : noPhotoUrl
                                            })`,
                                        }}
                                    ></div>
                                </div>
                            ) : y.name === "Address" ? (
                                <div
                                    key={`trc-${k}`}
                                    style={{
                                        fontSize: "13px",
                                        marginLeft: "24px",
                                        flex: getTableCellFlex(y.name),
                                        minWidth: getTableFixedWidth(y.name),
                                        maxWidth: getTableFixedWidth(y.name),
                                    }}
                                >
                                    <StyledLb to={`/deal/${comp.dealkey ? comp.dealkey : comp.dealKey}`}>
                                        <StyledListingIdLink data-testid="auditorSearchLink">
                                            {get(comp, "listing_name")}
                                        </StyledListingIdLink>
                                    </StyledLb>
                                    <div style={{ color: "red", fontWeight: "bold" }}>{`${
                                        getFromAttributeValues(comp, ["confidentialFlg", "confidential_flg"], false)
                                            ? "Confidential"
                                            : ""
                                    }`}</div>
                                    <div>{`${get(comp, "address1", {})},`}</div>
                                    <div>{`${get(comp, "city", {})}, ${get(comp, "state_code", "")} ${
                                        get(comp, "zip_postal_code", null) !== null ? get(comp, "zip_postal_code") : ""
                                    }`}</div>
                                    <div>{`${
                                        getFromAttributeValues(comp, ["confidentialFlg", "confidential_flg"], false)
                                            ? "Confidential "
                                            : ""
                                    } ${
                                        get(comp, "comp_status", false) === "OUTSIDE"
                                            ? "Outside"
                                            : get(comp, "comp_status", false) === "APPRAISAL"
                                              ? "Appraisal"
                                              : "KM"
                                    }, ${get(comp, "property_type")}`}</div>
                                </div>
                            ) : y.transform ? (
                                y.name === "Brokers" ? (
                                    <StyledBrokerColumn
                                        style={{
                                            flex: getTableCellFlex(y.name),
                                            minWidth: getTableFixedWidth(y.name),
                                            maxWidth: getTableFixedWidth(y.name),
                                        }}
                                        key={`trc-${k}`}
                                    >
                                        {get(comp, y.prop, " ") && (
                                            <div>
                                                <Tooltip
                                                    id={`audit-search-tooltip-${k}`}
                                                    placement="top"
                                                    title={
                                                        get(comp, y.prop, "").includes(",") ? get(comp, y.prop, "") : ""
                                                    }
                                                >
                                                    <StyledTooltipSpan>
                                                        {y.transform(get(comp, y.prop, ""))}
                                                    </StyledTooltipSpan>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </StyledBrokerColumn>
                                ) : (
                                    // deal type
                                    (<div
                                        key={`trc-${k}`}
                                        numeric={y.numeric}
                                        style={{
                                            fontSize: "13px",
                                            marginLeft: "4px",
                                            flex: getTableCellFlex(y.name),
                                            minWidth: getTableFixedWidth(y.name),
                                            maxWidth: getTableFixedWidth(y.name),
                                        }}
                                    >
                                        {y.transform(get(comp, y.prop))}
                                    </div>)
                                )
                            ) : y.name === "Documents" ? (
                                <div
                                    style={{
                                        paddingTop: "30px",
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "1vw",
                                        flex: getTableCellFlex(y.name),
                                    }}
                                >
                                    {sortedDocuments.map((document, document_index) => (
                                        <span>
                                            <Tooltip
                                                id={`tr-${i}-doc-${document_index}`}
                                                placement="top"
                                                className="statBoxTooltip"
                                                title={document?.documentTypeName}
                                            >
                                                <span
                                                    data-tooltip-id={`tr-${i}-doc-${document_index}`}
                                                    data-testid={`${document?.documentTypeName}`}
                                                >
                                                    <StyledLightButton
                                                        disabled={document?.mediaAssetKey == null}
                                                        onClick={() => viewDocument(null, document?.mediaAssetKey)}
                                                        color="primary"
                                                    >
                                                        <Doc
                                                            fill={
                                                                document?.mediaAssetKey != null ? "#2C99D2" : "#9E9E9E"
                                                            }
                                                        />
                                                    </StyledLightButton>
                                                </span>
                                            </Tooltip>
                                        </span>
                                    ))}
                                </div>
                            ) : null;
                        })}
                    </StyledAuditSearchRowContainer>
                    <Collapse in={expanded} timeout="auto">
                        <div style={{ borderTop: "1px solid rgb(212, 212, 212)" }}>
                            {sortedDocuments.map((document, document_index) => (
                                <div
                                    style={{
                                        fontSize: "13px",
                                        padding: `10px 10px 0px 0px`,
                                        borderBottom: "1px solid rgb(212, 212, 212)",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <span data-tooltip-id={`tr-${i}-expandedDoc-${document_index}`}>
                                        <StyledLightButton
                                            disabled={get(document, "mediaAssetKey", null) === null}
                                            onClick={() => viewDocument(null, get(document, "mediaAssetKey"))}
                                            color="primary"
                                        >
                                            <Doc
                                                fill={
                                                    get(document, "mediaAssetKey", null) !== null
                                                        ? "#2C99D2"
                                                        : "#9E9E9E"
                                                }
                                            />
                                        </StyledLightButton>
                                    </span>
                                    <StyledDocumentLinkButton
                                        disabled={get(document, "mediaAssetKey", null) === null}
                                        onClick={() => viewDocument(null, get(document, "mediaAssetKey"))}
                                        color="primary"
                                    >
                                        <StyledDocumentLinkSpan document={document}>
                                            {get(document, "documentTypeName", "")}
                                        </StyledDocumentLinkSpan>
                                    </StyledDocumentLinkButton>
                                </div>
                            ))}
                        </div>
                    </Collapse>
                </div>
            )}
        </Draggable>
    );
};

export default AuditSearchRow;
